/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useState } from "react";
import { Row } from "@silevis/reactgrid";

import GenericGrid from "../../GenericGrid";
import { getBulkItemRow } from "./utils";
import { AsyncValidateCellProps, ValidationErrors } from "../../GenericGrid/types";
import { BulkItem } from "./types";

const columns = [
  { columnId: 'categoryName', width: 150 },
  { columnId: 'itemName', width: 150 },
  { columnId: 'price', width: 150 },
  { columnId: 'availableStatus', width: 150 },
  { columnId: 'section', width: 150 },
  { columnId: 'description', width: 150 },
  { columnId: 'photo1', width: 150 },
  { columnId: 'photo2', width: 150 },
  { columnId: 'photo3', width: 150 },
  { columnId: 'photo4', width: 150 },
  { columnId: 'optionGroup1', width: 150 },
  { columnId: 'optionGroup2', width: 150 },
];

const headerRow: Row = {
  rowId: 'header',
  cells: [
    { type: 'header', text: '*Category Name' },
    { type: 'header', text: '*Item Name' },
    { type: 'header', text: '*Price' },
    { type: 'header', text: '*Available Status' },
    { type: 'header', text: 'Section' },
    { type: 'header', text: 'Description' },
    { type: 'header', text: 'Photo 1' },
    { type: 'header', text: 'Photo 2' },
    { type: 'header', text: 'Photo 3' },
    { type: 'header', text: 'Photo 4' },
    { type: 'header', text: 'Option Group 1' },
    { type: 'header', text: 'Option Group 2' },
  ],
};

const emptyBulkItem: BulkItem = {
  categoryName: '',
  itemName: '',
  price: '',
  availableStatus: '',
  section: '',
  description: '',
  photo1: '',
  photo2: '',
  photo3: '',
  photo4: '',
  optionGroup1: '',
  optionGroup2: '',
};

const REQUIRED_FIELDS = {
  categoryName: 'Category name is required',
  itemName: 'Item name is required',
  price: 'Price is required',
  availableStatus: 'Available status is required',
} as const;

const validateBulkItem = async ({ change, setTargetCellError }: AsyncValidateCellProps): Promise<{ newErrors: ValidationErrors }> => {
  const newErrors: ValidationErrors = {};
  const { columnId, newCell, rowId, type } = change;
  
  if (type === 'text' && columnId in REQUIRED_FIELDS) {
    const errorMessage = !newCell.text ? REQUIRED_FIELDS[columnId as keyof typeof REQUIRED_FIELDS] : '';
    Object.assign(newErrors, setTargetCellError(rowId.toString(), columnId as string, errorMessage));
  }

  return Promise.resolve({ newErrors });
};

const BulkItemUI = () => {
  const [bulkItems, setBulkItems] = useState<BulkItem[]>([emptyBulkItem]);

  const handleDataChange = (newData: BulkItem[]) => {
    setBulkItems(newData);
  };

  const handleAddingRow = (newData: BulkItem) => {
    setBulkItems([...bulkItems, newData]);
  };

  return (
    <GenericGrid<BulkItem>
      data={bulkItems}
      columns={columns}
      rows={getBulkItemRow}
      headerRow={headerRow}
      validator={validateBulkItem}
      onDataChange={handleDataChange}
      isValidateAtFirstLoad
      defaultData={emptyBulkItem}
      defaultColumnId="categoryName"
      handleAddingRow={handleAddingRow}
      dataRowHeight={25}
    />
  );
};

export default BulkItemUI;
